import React, { useRef, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormikContext } from 'formik';

import { InputFieldFactory } from '../../../../components/FormikField';
import { ConfirmDialog, ConfirmDialogWithTextInput, ImageUploadArea, IsSuperAdmin } from '../../../../components';

import type { OwnerFormValues } from './OwnerForm.types';
import { OwnerFormFields } from './OwnerForm.types';
import { TranslationKeys } from '../../../../translations';

const MODAL_CHANGE_EXTERNAL_CONNECTION_TEST_ID = 'modal-change-external-connection';
const MODAL_CLEAR_EXTERNAL_CONNECTION_TEST_ID = 'modal-clear-external-connection';

export const OwnerForm = () => {
  const intl = useIntl();
  const { values, setFieldValue } = useFormikContext<OwnerFormValues>();
  const [changeExternalConnections, setChangeExternalConnections] = useState(false);
  const [clearExternalConnections, setClearExternalConnections] = useState(false);
  const [pendingInstance, setPendingInstance] = useState<string | null>(null);
  const [previousInstance, setPreviousInstance] = useState<string | null>(null);
  const isConfirmingInstanceChangeRef = useRef(false);

  const isSuperAdmin = React.useMemo(() => IsSuperAdmin(), []);
  const onLogoChange = React.useCallback(
    (file?: File) => {
      setFieldValue(OwnerFormFields.Logo.InputName, file || '');
    },
    [setFieldValue],
  );
  const ownerIsSubOwner = values.parentId !== null;
  React.useEffect(() => {
    if (!isSuperAdmin && values.instance === '') {
      setFieldValue(OwnerFormFields.Instance.InputName, 'soderberg');
    }
  }, [isSuperAdmin, values.instance, setFieldValue]);

  const instancesSelectItems = React.useMemo(() => {
    return [
      {
        value: 'insurance-brydge',
        element: 'InsuranceBrydge',
      },
      {
        value: 'soderberg',
        element: 'Soderberg',
      },
    ];
  }, []);

  const pendingInstanceLabel = React.useMemo(() => {
    return instancesSelectItems.find(item => item.value === pendingInstance)?.element || '';
  }, [instancesSelectItems, pendingInstance]);

  const onInstanceChange = React.useCallback(
    ({ value }: { value: string }) => {
      const nextInstance = value || '';

      if (nextInstance === values.instance) return;

      setPreviousInstance(values.instance || '');
      setPendingInstance(nextInstance);

      if (nextInstance === '') {
        setClearExternalConnections(true);
        return;
      }

      setChangeExternalConnections(true);
    },
    [values.instance],
  );

  const closeDeleteModal = React.useCallback(() => {
    if (!isConfirmingInstanceChangeRef.current) {
      setFieldValue(OwnerFormFields.Instance.InputName, previousInstance ?? '');
    }

    isConfirmingInstanceChangeRef.current = false;
    setPendingInstance(null);
    setPreviousInstance(null);
    setChangeExternalConnections(false);
    setClearExternalConnections(false);
  }, [previousInstance, setFieldValue]);

  const handleConfirmInstanceChange = React.useCallback(() => {
    isConfirmingInstanceChangeRef.current = true;
    setFieldValue(OwnerFormFields.Instance.InputName, pendingInstance ?? '');
  }, [pendingInstance, setFieldValue]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.OwnerName.InputName,
              header: OwnerFormFields.OwnerName.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.OwnerName.Label }).toUpperCase(),
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.OwnerEmail.InputName,
              header: OwnerFormFields.OwnerEmail.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.OwnerEmail.Label }).toUpperCase(),
              type: 'email',
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.OwnerPhone.InputName,
              header: OwnerFormFields.OwnerPhone.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.OwnerPhone.Label }).toUpperCase(),
              required: true,
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.Address.InputName,
              header: OwnerFormFields.Address.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.Address.Label }).toUpperCase(),
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.City.InputName,
              header: OwnerFormFields.City.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.City.Label }).toUpperCase(),
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.ZipCode.InputName,
              header: OwnerFormFields.ZipCode.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.ZipCode.Label }).toUpperCase(),
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.ContactName.InputName,
              header: OwnerFormFields.ContactName.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.ContactName.Label }).toUpperCase(),
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.ContactEmail.InputName,
              header: OwnerFormFields.ContactEmail.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.ContactEmail.Label }).toUpperCase(),
              type: 'email',
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.ContactPhone.InputName,
              header: OwnerFormFields.ContactPhone.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.ContactPhone.Label }).toUpperCase(),
            }}
          />
        </Grid>
        {isSuperAdmin && !ownerIsSubOwner && (
          <Grid item xs={4}>
            <InputFieldFactory
              field={{
                type: 'autocomplete',
                name: OwnerFormFields.Instance.InputName,
                header: intl.formatMessage({ id: OwnerFormFields.Instance.Label }),
                value: values.instance,
                items: instancesSelectItems,
                disableClearable: false,
                clearOnEscape: true,
              }}
              onChange={onInstanceChange}
            />
          </Grid>
        )}
        <Grid item xs={4}>
          <InputFieldFactory
            field={{
              name: OwnerFormFields.KvkNumber.InputName,
              header: OwnerFormFields.KvkNumber.Label,
              placeholder: intl.formatMessage({ id: OwnerFormFields.KvkNumber.Label }).toUpperCase(),
              disabled: !isSuperAdmin,
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <ImageUploadArea label={OwnerFormFields.Logo.Label} initialFile={values.logo} onChange={onLogoChange} />
        </Grid>
      </Grid>

      <ConfirmDialogWithTextInput
        open={changeExternalConnections}
        testId={MODAL_CHANGE_EXTERNAL_CONNECTION_TEST_ID}
        title={<FormattedMessage id={TranslationKeys.owners_externalConnection_confirmChangeInstanceTitle} />}
        label={<FormattedMessage id={TranslationKeys.insurers_connectionName} />}
        errorMessage={<FormattedMessage id={TranslationKeys.connections_confirmDeleteMessage_invalidAnswer} />}
        solution={pendingInstanceLabel}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmInstanceChange}
      >
        <Typography>
          <FormattedMessage id={TranslationKeys.owners_externalConnection_confirmChangeInstance} />
        </Typography>

        <Typography lineHeight={2}>
          <FormattedMessage
            id={TranslationKeys.connections_confirmDeleteMessage_paragraph2}
            values={{ connectionName: pendingInstanceLabel, b: chunks => <b>{chunks}</b> }}
          />
        </Typography>
      </ConfirmDialogWithTextInput>

      <ConfirmDialog
        open={clearExternalConnections}
        testId={MODAL_CLEAR_EXTERNAL_CONNECTION_TEST_ID}
        title={<FormattedMessage id={TranslationKeys.owners_externalConnection_confirmClearInstanceTitle} />}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmInstanceChange}
      >
        <Typography>
          <FormattedMessage id={TranslationKeys.owners_externalConnection_confirmClearInstance} />
        </Typography>
      </ConfirmDialog>
    </>
  );
};
